import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Empty, Modal, Skeleton, Tag, Typography, message } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import { professorPortalService } from '../../services/api';
import ProfessorAlunoFrequenciaCard from './ProfessorAlunoFrequenciaCard';
import '../../styles/ProfessorPortal.css';

dayjs.locale('pt-br');

const { Title, Text } = Typography;

/**
 * Módulo "Experiência da Aula" — MVP cobre apenas a chamada de presença.
 * Estrutura pensada para acomodar, em etapas futuras, conteúdo ministrado,
 * observações gerais, ocorrências, fotos/anexos e encerramento pedagógico
 * da aula — sem que nada disso seja implementado aqui.
 */

const STATUS_AULA_LABELS = {
  PREVISTA: { label: 'Prevista', color: 'blue' },
  REALIZADA: { label: 'Realizada', color: 'green' },
  CANCELADA: { label: 'Cancelada', color: 'red' },
};

const formatarHora = (valor) => (typeof valor === 'string' ? valor.slice(0, 5) : '--:--');

const resolverErroAbertura = (error) => {
  const status = error?.response?.status;
  if (status === 404) {
    return { mensagem: 'Esta aula não foi encontrada ou não pertence a você.', permiteTentarNovamente: false };
  }
  if (status === 403) {
    return { mensagem: 'Você não possui acesso a esta aula.', permiteTentarNovamente: false };
  }
  if (status === 409) {
    return {
      mensagem: 'Não foi possível identificar seu vínculo de professor. Entre em contato com a administração.',
      permiteTentarNovamente: false,
    };
  }
  return { mensagem: 'Não foi possível carregar a chamada. Tente novamente.', permiteTentarNovamente: true };
};

const resolverErroSalvar = (error) => {
  const status = error?.response?.status;
  if (status === 409) return 'Este encontro foi cancelado e não aceita mais alterações de chamada.';
  if (status === 422) return 'Não foi possível salvar: revise as informações de frequência e tente novamente.';
  if (status === 403) return 'Você não possui acesso para salvar esta chamada.';
  if (status === 404) return 'Esta aula não foi encontrada ou não pertence a você.';
  return 'Não foi possível salvar a chamada. Tente novamente.';
};

const paraLinhasFrequencia = (alunos) =>
  (alunos || []).map((aluno) => ({
    matricula_id: aluno.matricula_id,
    aluno_id: aluno.aluno_id,
    nome_completo: aluno.nome_completo,
    status: aluno.status_frequencia || null,
    observacao: aluno.observacao || '',
  }));

const ProfessorAulaPage = () => {
  const { encontroId } = useParams();
  const navigate = useNavigate();

  const [encontro, setEncontro] = useState(null);
  const [frequencias, setFrequencias] = useState([]);
  const [snapshotInicial, setSnapshotInicial] = useState([]);
  const [chamadaJaExistia, setChamadaJaExistia] = useState(false);
  const [loading, setLoading] = useState(true);
  const [erroCarregamento, setErroCarregamento] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const salvandoRef = useRef(false);

  const carregarChamada = useCallback(async () => {
    setLoading(true);
    setErroCarregamento(null);
    try {
      const response = await professorPortalService.obterChamada(encontroId);
      const dados = response.data || {};
      const linhas = paraLinhasFrequencia(dados.alunos);
      setEncontro(dados.encontro || null);
      setFrequencias(linhas);
      setSnapshotInicial(linhas);
      setChamadaJaExistia((dados.encontro?.total_frequencias_informadas || 0) > 0);
    } catch (error) {
      setErroCarregamento(resolverErroAbertura(error));
    } finally {
      setLoading(false);
    }
  }, [encontroId]);

  useEffect(() => {
    carregarChamada();
  }, [carregarChamada]);

  const somenteLeitura = encontro?.status === 'CANCELADA';

  const resumo = useMemo(() => {
    let presentes = 0;
    let ausentes = 0;
    let justificadas = 0;
    let naoInformados = 0;
    frequencias.forEach((item) => {
      if (item.status === 'PRESENTE') presentes += 1;
      else if (item.status === 'AUSENTE') ausentes += 1;
      else if (item.status === 'JUSTIFICADA') justificadas += 1;
      else naoInformados += 1;
    });
    return { total: frequencias.length, presentes, ausentes, justificadas, naoInformados };
  }, [frequencias]);

  const isDirty = useMemo(() => {
    if (frequencias.length !== snapshotInicial.length) return true;
    return frequencias.some((item, index) => {
      const original = snapshotInicial[index];
      return !original || item.status !== original.status || (item.observacao || '') !== (original.observacao || '');
    });
  }, [frequencias, snapshotInicial]);

  const existemAusentesOuJustificadas = frequencias.some(
    (item) => item.status === 'AUSENTE' || item.status === 'JUSTIFICADA'
  );
  const labelMarcarTodos = existemAusentesOuJustificadas
    ? 'Marcar não informados como presentes'
    : 'Marcar todos presentes';

  const atualizarStatus = (matriculaId, novoStatus) => {
    setFrequencias((prev) =>
      prev.map((item) => (item.matricula_id === matriculaId ? { ...item, status: novoStatus } : item))
    );
  };

  const atualizarObservacao = (matriculaId, texto) => {
    setFrequencias((prev) =>
      prev.map((item) => (item.matricula_id === matriculaId ? { ...item, observacao: texto } : item))
    );
  };

  const marcarTodosPresentes = () => {
    setFrequencias((prev) =>
      prev.map((item) => {
        if (existemAusentesOuJustificadas) {
          return item.status ? item : { ...item, status: 'PRESENTE' };
        }
        return { ...item, status: 'PRESENTE' };
      })
    );
  };

  const handleVoltar = () => {
    if (!isDirty) {
      navigate('/professor');
      return;
    }
    Modal.confirm({
      title: 'Descartar alterações?',
      content: 'Você tem alterações de chamada não salvas. Deseja sair mesmo assim?',
      okText: 'Sair sem salvar',
      cancelText: 'Continuar editando',
      onOk: () => navigate('/professor'),
    });
  };

  const handleSalvar = async () => {
    if (salvandoRef.current) return;
    if (resumo.naoInformados > 0) {
      message.warning('Informe a frequência de todos os alunos antes de salvar.');
      return;
    }
    salvandoRef.current = true;
    setSalvando(true);
    try {
      const payload = {
        frequencias: frequencias.map((item) => ({
          matricula_id: item.matricula_id,
          status: item.status,
          observacao: item.observacao && item.observacao.trim() ? item.observacao.trim() : null,
        })),
      };
      await professorPortalService.salvarChamada(encontroId, payload);
      message.success('Chamada salva com sucesso.');
      navigate('/professor');
    } catch (error) {
      if (error?.response?.status === 409) {
        setEncontro((prev) => (prev ? { ...prev, status: 'CANCELADA' } : prev));
      }
      message.error(resolverErroSalvar(error));
    } finally {
      salvandoRef.current = false;
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div className="professor-aula-page">
        <div role="status" aria-live="polite">
          <Text type="secondary">Carregando chamada…</Text>
          <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 12 }} />
        </div>
      </div>
    );
  }

  if (erroCarregamento) {
    return (
      <div className="professor-aula-page">
        <Card>
          <Text>{erroCarregamento.mensagem}</Text>
          <div className="professor-aula-erro-acoes">
            {erroCarregamento.permiteTentarNovamente && (
              <Button type="primary" onClick={carregarChamada}>
                Tentar novamente
              </Button>
            )}
            <Button onClick={() => navigate('/professor')}>Voltar</Button>
          </div>
        </Card>
      </div>
    );
  }

  const statusAula = STATUS_AULA_LABELS[encontro?.status] || { label: encontro?.status, color: 'default' };
  const podeSalvar = !somenteLeitura && !salvando && resumo.naoInformados === 0;

  return (
    <div className="professor-aula-page">
      <div className="professor-aula-header">
        <Button
          icon={<LeftOutlined aria-hidden="true" />}
          onClick={handleVoltar}
          className="professor-aula-voltar"
        >
          Voltar
        </Button>

        <Title level={2} className="professor-aula-turma">
          {encontro?.nome_turma}
        </Title>
        <div className="professor-aula-meta">
          {encontro?.nome_unidade && <Text type="secondary">{encontro.nome_unidade}</Text>}
          <Text type="secondary">
            {encontro?.data_aula ? dayjs(encontro.data_aula).format('DD/MM/YYYY') : '--/--/----'} ·{' '}
            {formatarHora(encontro?.hora_inicio)}–{formatarHora(encontro?.hora_fim)}
          </Text>
          <Tag color={statusAula.color}>{statusAula.label}</Tag>
        </div>
      </div>

      <div className="professor-aula-resumo" role="status" aria-live="polite">
        <Text>Total: {resumo.total}</Text>
        <Text>Presentes: {resumo.presentes}</Text>
        <Text>Ausentes: {resumo.ausentes}</Text>
        <Text>Justificadas: {resumo.justificadas}</Text>
        <Text>Não informados: {resumo.naoInformados}</Text>
      </div>

      {somenteLeitura && (
        <Alert
          type="info"
          showIcon
          message="Esta aula foi cancelada. A chamada está disponível apenas para consulta."
          className="professor-aula-aviso-cancelada"
        />
      )}

      {!somenteLeitura && (
        <div className="professor-aula-acoes-principais">
          <Button onClick={marcarTodosPresentes} disabled={resumo.naoInformados === 0}>
            {labelMarcarTodos}
          </Button>
        </div>
      )}

      {!somenteLeitura && resumo.naoInformados > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${resumo.naoInformados} aluno(s) ainda não informado(s)`}
          className="professor-aula-pendentes"
        />
      )}

      {frequencias.length === 0 ? (
        <Empty description="Não há alunos elegíveis para esta aula." />
      ) : (
        <div className="professor-aula-alunos-lista">
          {frequencias.map((item) => (
            <ProfessorAlunoFrequenciaCard
              key={item.matricula_id}
              nomeCompleto={item.nome_completo}
              status={item.status}
              observacao={item.observacao}
              somenteLeitura={somenteLeitura}
              onChangeStatus={(novoStatus) => atualizarStatus(item.matricula_id, novoStatus)}
              onChangeObservacao={(texto) => atualizarObservacao(item.matricula_id, texto)}
            />
          ))}
        </div>
      )}

      {!somenteLeitura && (
        <div className="professor-aula-acao-fixa">
          <Button type="primary" block disabled={!podeSalvar} loading={salvando} onClick={handleSalvar}>
            {chamadaJaExistia ? 'Salvar alterações' : 'Salvar chamada'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProfessorAulaPage;
